// Eszköz-lista (workforce leltár + igénylés-workflow, BELSŐ nézet) — a KÖZÖS DataTable-re épül (a
// clients.index / tickets / accounts a referencia-minta): szerver-oldali (BFF) kereső (300 ms
// debounce, név/leltári szám/sorozatszám/birtokló/igénylő) + státusz/típus-szűrő + darabszám-meta a toolbarban,
// szerver-rendezés (name / type / status / holderName / requesterName / teamName / netPrice / purchaseDate),
// végtelen görgetés (100/lap). A /assets BFF-végpontról fogyaszt (busz-sorok + birtokló/igénylő identity-név +
// név-részek + SZÍNES avatar + csapat-név feloldva; tenant-scope grant-fal RLS Odin-oldalon; ops-token read:all → see-all).
// Page-gate: `asset_inventory.view` (a legacy `view_asset_inventory` migrált kulcsa — NEM `assets.view`).
//
// KÉT TAB — SZERVER-oldali `scope`-partíció (a legacy hw/sw/igények 3-tabból best-practice-ebb 2-tabos bontás; a
// `scope` query-param vezérli, a BFF a `AssetQueryService` mintájára partícionál):
//   1. „Meglévő eszközök" (scope=inventory) — a nálunk lévők: status ∈ {received, spare, in_use, retired}.
//      Oszlopok: Eszköz (név + „> Részletek": Nyilvántartási szám · Sorozatszám/Termékkulcs · Vásárlás dátuma ·
//      Számlaszám · Beszerzés helye · Selejtezés dátuma) · Típus · Birtokló (színes avatar + név, formatName;
//      holder hiányában az igénylő; alatta a felhasználás helye) · Státusz.
//   2. „Igényelt eszközök" (scope=request) — a folyamatban lévők: status ∈ {requested, approved, rejected,
//      being_procured, purchased}. Oszlopok: Eszköz · Típus · Részleg · Ár (nettó/áfa/bruttó) · Igénylő · Státusz.
//
// TELJES ÍRÁS-RÉTEG (a gomb-egységesítés a KÖZÖS komponenseken): a felső „Új eszköz"/„Igénylés beadása"
// `ListNewButton` (≥md) + mobil FAB (`AppShell primaryAction`) NÉZET-ÉRZÉKENYEN (Meglévő→új eszköz / Igényelt→új
// igény). Sor-akciók a KÖZÖS `list-row-actions`-ból: LELTÁR → ceruza (create/edit FormSheet, assets.manage) + kuka
// (ConfirmDialog + optimista eltávolítás, assets.delete); a folyamatban lévő workflow-sor ceruzája TILTOTT
// (`RowActionButton disabled`, a szerver is 409-cel véd). IGÉNYLÉSEK → requested: jóváhagyás/elutasítás
// (asset_requests.decide; a reject indok-mezős dialógussal), approved/being_procured/purchased: következő beszerzési
// lépés (asset_requests.manage) + kuka. Minden workflow-írás If-Match/OCC-s (409 → conflict toast) + optimista
// státusz-átírás rollbackkel; a scope-ból kilépő sor (pl. received) az onSettled-invalidáláskor kerül a másik tabra.
import { type AssetRowSchema } from '@atlas/contracts';
import {
  ArrowDownTrayIcon,
  Squares2X2Icon,
  ArrowRightCircleIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  PencilSquareIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AssetCatalogSheet } from '@/components/assets/asset-catalog-sheet';
import { AssetPriceBreakdown, AssetTypeWithCategory } from '@/components/assets/asset-cells';
import { AssetFormSheet } from '@/components/assets/asset-form-sheet';
import { AssetRejectDialog } from '@/components/assets/asset-reject-dialog';
import { AssetRequestSheet } from '@/components/assets/asset-request-sheet';
import { AssetStatusBadge } from '@/components/assets/asset-status-badge';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailsExpander } from '@/components/shared/details-expander';
import { EmptyState } from '@/components/shared/empty-state';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListExportButton } from '@/components/shared/list-export-button';
import { ListNewButton } from '@/components/shared/list-new-button';
import {
  DeleteRowAction,
  EditRowAction,
  RowActionButton,
  RowActions,
  ViewRowAction,
} from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonChip } from '@/components/shared/person-chip';
import { TeamBadge } from '@/components/shared/team-badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { assetKeys } from '@/features/assets/api';
import { catalogText, useAssetCatalog } from '@/features/assets/catalog';
import { USAGE_OPTIONS } from '@/features/assets/form';
import { fmtHuf, fmtPercent, priceParts } from '@/features/assets/price';
import { employeeSearch } from '@/features/employees/search';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { useListExport } from '@/hooks/use-list-export';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { isForbiddenStatus } from '@/lib/error-status';
import { isExternalUrl, toHref } from '@/lib/external-url';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

// Deep-link: `?open=<id>` → a leltár-fülön a szerkesztő-sheet a megadott eszközre nyílik (a command palette „2.
// rungja"; CSAK az aktív fül betöltött sorai közül keres — graceful no-op). A KÉRÉSEK fülön (`?scope=request&open=<id>`,
// a levél és a harang mélylinkje) az igény-sáv nyílik, az igényt id alapján töltve (a betöltött laptól független).
const assetsSearchSchema = z.object({
  open: z.string().optional(),
  /**
   * Melyik FÜL (`inventory` | `request`) — az URL-ben, mert a fül a repo-konvenció szerint URL-állapot
   * (linkelhető, F5-biztos nézet), és a kezdőlap „rám vár" kártyája erre mélylinkel („jóváhagyásra váró
   * eszköz-igények" → `?scope=request&status=requested`). Ismeretlen érték → a leltár-fül (alap).
   */
  scope: z.string().optional(),
  q: z.string().optional(),
  status: z.string().optional(),
  category: z.string().optional(),
  type: z.string().optional(),
  /** Csapat-szűrő (UUID) — URL-állapot, hogy a „mi tartozik ehhez a csapathoz?" nézet LINKELHETŐ legyen. */
  team: z.string().optional(),
  /** Munkatárs-szűrő (identity UUID): a birtokos VAGY a „kinek" — LINKELHETŐ nézet (#112). */
  holder: z.string().optional(),
  sort: z.string().optional(),
  order: z.string().optional(),
});

export const Route = createFileRoute('/assets')({
  validateSearch: (search) => assetsSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: AssetsRoute,
});

// Route-wrapper: a deep-link search-paramot itt olvassuk + a param-törlést itt navigáljuk (a lista-oldal router-hook-mentes).
function AssetsRoute(): JSX.Element {
  const { open } = Route.useSearch();
  const navigate = useNavigate();
  const clearDeepLink = useCallback(() => {
    void navigate({
      to: '/assets',
      search: (prev) => ({ ...prev, open: undefined }),
      replace: true,
    });
  }, [navigate]);
  return <AssetsPage deepLinkOpenId={open} onClearDeepLink={clearDeepLink} />;
}

type AssetRow = z.infer<typeof AssetRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.assets.query>>;

export interface AssetsPageProps {
  /** Deep-link: a `?open=` paraméterből érkező eszköz-id (a szerkesztő-sheet erre nyílik, ha a sor az aktív fülön betöltött). */
  deepLinkOpenId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}

const PAGE_SIZE = 100;
const ALL = '__all__';

// A legacy enum-kulcsok (adat-parity a régi CRM-mel — AssetStatusEnum / AssetTypeEnum) — a címkék az i18n-ből.
// A státusz-szűrő OPCIÓI tab-scope-onként (a BFF is ezen a partíción szűr):
const INVENTORY_STATUS_OPTIONS = ['received', 'spare', 'in_use', 'retired'];
const REQUEST_STATUS_OPTIONS = ['requested', 'approved', 'rejected', 'being_procured', 'purchased'];

/**
 * A `purchased` státusz KETTŐS tagságú: az igény-workflow lépése (requesterrel) ÉS kézi leltár-státusz
 * (requester nélkül — pl. „+ Új eszköz" default). A szerver a `scope`-partíciót ennek megfelelően végzi (a
 * kézzel felvett purchased sor a LELTÁRBAN, a workflow-s az IGÉNYLÉSEK közt). A leltár-sor szerkesztését ez a
 * kliens-oldali guard is védi: a (ritkán) workflow-státuszú sor ceruzája TILTOTT (a szerver is 409-cel véd).
 */
function isWorkflowRow(a: { status: string; requesterId: string | null }): boolean {
  if (['requested', 'approved', 'rejected', 'being_procured'].includes(a.status)) return true;
  return a.status === 'purchased' && a.requesterId != null;
}

/** A workflow SOR-AKCIÓ kulcsa: jóváhagyás vagy a következő beszerzési lépés (a reject külön dialógus). */
type WorkflowAction = 'approve' | 'being_procured' | 'purchased' | 'received';

/** A sor státuszából a KÖVETKEZŐ beszerzési lépés (a BFF szigorú állapotgépe szerint); nincs → null. */
const NEXT_STEP: Record<string, Exclude<WorkflowAction, 'approve'>> = {
  approved: 'being_procured',
  being_procured: 'purchased',
  purchased: 'received',
};
/** ISO → hu-HU rövid dátum (a beszerzés dátumához). */
function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat('hu-HU').format(d);
}

/**
 * Az „Eszköz" oszlop cellája: a NÉV (itemTitle) + egy „> Részletek" toggle — ugyanaz a kinyíló minta, mint az
 * eseménynapló első oszlopában. A panel a címkézett azonosító- + VÁSÁRLÁS-mezőket mutatja (csak a kitöltötteket):
 * Nyilvántartási szám · Sorozatszám/Termékkulcs · Vásárlás dátuma · Számlaszám · Beszerzés helye · Selejtezés
 * dátuma. (A vásárlás-adatok nem fő infók → a Részletek alatt, de megtalálhatók — user-döntés.) A toggle csak
 * akkor jelenik meg, ha van legalább egy megjeleníthető mező.
 */
function AssetNameCell({
  asset,
  showTeam = false,
}: {
  asset: AssetRow;
  /**
   * A csapat színes badge-e a „Részletek" gomb mellett — KÜLÖN OSZLOP HELYETT, MINDKÉT fülön.
   *
   * Korábban csak az igénylés-fülön jelent meg, a leltár-fülön viszont a csapat SEHOL nem látszott (oszlopa
   * sincs). Mért következmény: egy csapat törlését az eszköz-guard megállította, de nem lehetett kideríteni,
   * MELYIK eszköz tartja — a felület a kérdést fel tudta tenni, megválaszolni nem.
   */
  showTeam?: boolean;
}): JSX.Element {
  const rows = useAssetDetailRows(asset);
  const dept =
    showTeam && asset.teamName ? (
      <TeamBadge name={asset.teamName} color={asset.teamColor} className="max-w-full truncate" />
    ) : null;
  return (
    <div className="min-w-0 space-y-1">
      <Text variant="itemTitle" as="div" className="truncate">
        {asset.name || <span className="text-muted-foreground">—</span>}
      </Text>
      {/* A kibontó + a részlet-panel a KÖZÖS komponensből (`DetailsExpander`): ugyanez a minta kell az
          üzenetek és a jegyek első oszlopában is — a saját másolatok már elcsúsztak egymástól. */}
      <DetailsExpander rows={rows} trailing={dept ?? undefined} />
    </div>
  );
}

/**
 * Az eszköz azonosító-/vásárlás-mezői label:érték párokként, MEGJELENÍTÉSRE kész értékkel (a link-alakú
 * értékből `<a>`). A CÍM-cella (desktop kibontó) és a MOBIL-kártya is ezt használja — egy forrás, kétféle
 * megjelenítési kontextus. Az EXPORT viszont a nyers `assetDetailRows`-ból dolgozik (ott string kell).
 */
function useAssetDetailRows(asset: AssetRow): { label: string; value: ReactNode }[] {
  const { t } = useTranslation();
  const { lookup } = useAssetCatalog();
  return assetDetailRows(asset, t, lookup.isSoftwareType(asset.type)).map((r) => ({
    label: r.label,
    // A szabad szöveg (megjegyzés) SOSEM lesz link — a link-felismerés a link-jellegű mezőké (weboldal, kulcs).
    value: r.plain ? (
      <span className="whitespace-pre-line">{r.value}</span>
    ) : (
      <DetailValue value={r.value} />
    ),
  }));
}

/**
 * Ugyanaz TISZTA függvényként (a `t` injektálva) — mert az EXPORT is pontosan ezeket a mezőket viszi, és egy
 * `exportValue` callbackből hookot hívni nem lehet. Egy helyen dől el, MI tartozik az eszköz „Részletek"
 * blokkjába: ha ide új mező jön, az exportba is bekerül (nem marad ki csendben).
 */
function assetDetailRows(
  asset: AssetRow,
  t: TFunction,
  isSoftware: boolean,
): { label: string; value: string; plain?: true }[] {
  // Sorozatszám (hardver) vagy termékkulcs (szoftver-jellegű kategória a katalógusban).
  const idLabel = isSoftware ? t('assets.licenseKey') : t('assets.serialNumber');
  const idValue = isSoftware ? asset.licenseKey : asset.serialNumber;
  const purchased = fmtDate(asset.purchaseDate);
  const retire = fmtDate(asset.retirementDate);
  const expiry = fmtDate(asset.expiryDate);
  const rows: { label: string; value: string; plain?: true }[] = [
    ...(asset.assetNumber ? [{ label: t('assets.assetNumber'), value: asset.assetNumber }] : []),
    ...(idValue ? [{ label: idLabel, value: idValue }] : []),
    // Vásárlás adatai (a legacy külön oszlop helyett itt, a Részletek alatt — nem fő infó, de megtalálható):
    ...(purchased ? [{ label: t('assets.purchaseDateLabel'), value: purchased }] : []),
    ...(asset.invoiceNumber
      ? [{ label: t('assets.invoiceNumber'), value: asset.invoiceNumber }]
      : []),
    // A WEBOLDAL (gyártó/termék link) CSAK MEGJELENÍTÉS: a leltár-írás útja szándékosan nem fogadja
    // (`INVENTORY_PATCH_KEYS` — a mező az IGÉNYLÉS-folyamathoz tartozik), a migrált érték viszont 37
    // eszközön ott van, és eddig SEHOL nem látszott. A megjelenítést a `DetailValue` dönti el: link-alakú
    // értékből (`http(s)://` vagy `www.`) KATTINTHATÓ `<a>`, minden másból sima szöveg.
    ...(asset.website ? [{ label: t('assets.fields.website'), value: asset.website }] : []),
    ...(asset.purchaseLocation
      ? [{ label: t('assets.purchaseLocation'), value: asset.purchaseLocation }]
      : []),
    ...(retire ? [{ label: t('assets.retirementDate'), value: retire }] : []),
    // Előfizetés / licenc lejárata — erre megy a napi lejárat-értesítő (30 és 7 nappal előtte, majd a napján).
    ...(expiry ? [{ label: t('assets.fields.expiryDate'), value: expiry }] : []),
    // Az IGÉNYLÉS megjegyzése (a „Kinek" külön oszlop az igénylés-fülön — #115).
    ...(asset.requestNote
      ? [{ label: t('assets.request.fields.note'), value: asset.requestNote, plain: true as const }]
      : []),
    // Felhasználás helye — KÜLÖN OSZLOP volt; user-döntés: a táblában nem kell (a szűrő az
    // elsődleges belépési pontja), a „Részletek" listájában pedig az UTOLSÓ a helye. Az export automatikusan
    // viszi (a `Részletek` sorai a fő oszlop `exportValue`-jában is benne vannak) → nem esik ki csendben.
    ...(asset.usageLocation
      ? [
          {
            label: t('assets.usageLocationCol'),
            value: t(`assets.usageLabel.${asset.usageLocation}`, {
              defaultValue: asset.usageLocation,
            }),
          },
        ]
      : []),
  ];
  return rows;
}

/**
 * Egy Részletek-ÉRTÉK megjelenítése: link-alakú bemenetből KATTINTHATÓ hivatkozás, minden másból sima szöveg.
 *
 * MIÉRT KELL: három szabad-szöveges mezőbe (termékkulcs · beszerzés helye · weboldal) a kollégák hol linket,
 * hol nevet írnak — az élő adaton mérve 53 valódi link és 38 sima szöveg ugyanazokban a mezőkben. A tárolás
 * marad SZÖVEG (a szerződés és az írás-út érintetlen); csak a MEGJELENÍTÉS dönti el, hogy `<a>` legyen-e.
 *
 * MIÉRT NEM MEZŐNKÉNTI WHITELIST: a szabály minden Részletek-sorra fut. A többi mező (nyilvántartási szám,
 * dátum, számlaszám, felhasználás helye) sosem URL-alakú, tehát nincs mit elrontani — cserébe nincs
 * karbantartandó lista sem, és egy jövőbeli mező is működik magától.
 *
 * A `stopPropagation` NEM kozmetika: a sorra kattintás megnyitja a szerkesztő-sheetet, tehát enélkül egy
 * link-kattintás EGYSZERRE navigálna és sheetet nyitna.
 *
 * EXPORTÁLT — a komponens-teszt közvetlenül ezt a szerződést méri (a teljes lap felbootolása nélkül): mi lesz
 * linkből `<a>` és mi marad szöveg. A route-fájl amúgy is exportál (`Route`, `AssetsPage`).
 */
export function DetailValue({ value }: { value: string }): JSX.Element {
  if (!isExternalUrl(value)) return <>{value}</>;
  return (
    <a
      href={toHref(value)}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="text-brand-hover truncate hover:underline"
    >
      {value}
    </a>
  );
}

/** A label:érték lista renderelése (kibontó nélkül) — a hívó dönti el, mikor látszik. */

/**
 * MOBIL-kártya: a fejlécbe CSAK az eszköz neve (+ a részleg-badge az igénylés-fülön) — kibontó NÉLKÜL, mert a
 * kártya „Részletek" gombja már a kapcsoló. Az azonosító-/vásárlás-mezők a `cardExtra`-ba mennek.
 * (A lelet: két azonos feliratú kibontó állt egymás mellett a kártyán.)
 */
function AssetNameCardCell({
  asset,
  showTeam = false,
}: {
  asset: AssetRow;
  showTeam?: boolean;
}): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <Text variant="itemTitle" as="div" className="truncate">
        {asset.name || <span className="text-muted-foreground">—</span>}
      </Text>
      {showTeam && asset.teamName && (
        <TeamBadge name={asset.teamName} color={asset.teamColor} className="max-w-full truncate" />
      )}
    </div>
  );
}

/** A kártya „Részletek" blokkjába kerülő azonosító-/vásárlás-mezők (a hook miatt külön komponens). */

export function AssetsPage({ deepLinkOpenId, onClearDeepLink }: AssetsPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  // Page-gate: a MIGRÁLT nézet-jog kulcsa `asset_inventory.view` (a legacy `view_asset_inventory` → remapPerm),
  // NEM `assets.view` (az nem is létezik az Odin jog-katalógusban → mindenkit 403-ra vitt). Az export a view-val jár.
  const canView = can('asset_inventory.view');
  // Jog-vezérelt akciók: assets.manage → „+ Új eszköz" + ceruza (create/edit sheet); assets.delete → kuka;
  // asset_requests.manage → „Igénylés beadása" + beszerzés-lépések; asset_requests.decide → jóváhagyás/elutasítás.
  const canManage = can('assets.manage');
  const canDelete = can('assets.delete');
  const canRequest = can('asset_requests.manage');
  const canDecide = can('asset_requests.decide');

  // Tab-scope: `inventory` (a nálunk lévő eszközök — 1., alap) | `request` (az igénylés-/beszerzés-folyamat — 2.).
  // Az ÁLLAPOT az URL-ben van (`?scope=`), így a fül linkelhető és F5-biztos (a kezdőlap ide mélylinkel).

  // Szerkesztő-sheet: `null` → zárt; `{ asset: null }` → LÉTREHOZÁS; `{ asset }` → SZERKESZTÉS.
  const [formTarget, setFormTarget] = useState<{ asset: AssetRow | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AssetRow | null>(null);
  // Igénylés-workflow: beadás-sheet + megerősítendő akció (approve / következő lépés) + elutasítás-cél.
  // Igény-sáv: `null` → zárva; `{ id: null }` → ÚJ igény; `{ id }` → az igény nézete (a sor a seed). A mélylink
  // (`?scope=request&open=`) id alapján nyit, a lista betöltött lapjától függetlenül.
  const [requestSheet, setRequestSheet] = useState<{
    id: string | null;
    seed: AssetRow | null;
  } | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [workflowTarget, setWorkflowTarget] = useState<{
    asset: AssetRow;
    action: WorkflowAction;
  } | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AssetRow | null>(null);
  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    // A FÜL is a szűrő-készlet része (a `useListFilters` a default-értékű kulcsot nem írja az URL-be, tehát
    // a leltár-fül tiszta linket ad, a request-fül pedig `?scope=request`-et).
    scope: 'inventory',
    status: ALL,
    category: ALL,
    type: ALL,
    team: ALL,
    holder: ALL,
    sort: 'assetNumber',
    order: 'desc',
  });
  const q = urlFilters.q;
  const scope: 'inventory' | 'request' = urlFilters.scope === 'request' ? 'request' : 'inventory';
  const status = urlFilters.status;
  const category = urlFilters.category;
  const type = urlFilters.type;
  const team = urlFilters.team;
  const holder = urlFilters.holder;
  const sort = urlFilters.sort;
  const order = urlFilters.order as 'asc' | 'desc';
  // Page-gate: asset_inventory.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → jog-hiba.
  const gated = !permsLoading && !canView;
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  const [usageLocation, setUsageLocation] = useState<string>(ALL);

  // A kategória- és típus-opciók a szerkeszthető KATALÓGUSBÓL jönnek; a típus-opciók a kategóriától függnek, a
  // kategória váltásakor a nem-illő típus-szűrőt visszaállítjuk.
  const { lookup } = useAssetCatalog(!gated);
  const lang = i18n.language;
  const typeSource =
    category === ALL ? lookup.categories.flatMap((c) => c.types) : lookup.typesOfCategory(category);
  const onCategoryChange = useCallback(
    (v: string) => {
      // Kategória-váltás: a nem-illő típus-szűrő UGYANABBAN a navigációban esik vissza `ALL`-ra (egy
      // URL-írás, nem kettő — különben a két navigáció versenyezne, és az egyik felülírná a másikat).
      const keepsType =
        v === ALL || type === ALL || lookup.typesOfCategory(v).some((ty) => ty.key === type);
      setFilters(keepsType ? { category: v } : { category: v, type: ALL });
    },
    [type, setFilters, lookup],
  );
  // Szerver-rendezés: a `sort` a kulcs, az `order` az irány. Alap: assetNumber DESC (a legacy leltári sorrend).

  // Tab-váltás: a státusz-szűrő opciói scope-onként mások → váltáskor a státusz-szűrőt visszaállítjuk; a
  // felhasználás-hely szűrő csak a leltár-fülön van, ezért azt is (a request-tabon nincs).
  const onScopeChange = useCallback(
    (v: string) => {
      // A fül ÉS a státusz-szűrő EGY navigációban áll be (két URL-írás versenyezne egymással); a
      // státusz-opciók scope-onként mások, ezért a szűrő visszaáll.
      setFilters({ scope: v === 'request' ? 'request' : 'inventory', status: ALL });
      setUsageLocation(ALL);
    },
    [setFilters],
  );

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // A CSAPAT-SZŰRŐ opciói — ugyanaz a kis referencia-lista, amit a szerkesztő is használ (`/assets/refs`),
  // azonos cache-kulcson: a lap és a sheet így EGY lekérést oszt meg, nem kettőt indít.
  const refsQ = api.assetRefs.useQuery(
    ['asset-refs'],
    {},
    { queryKey: ['asset-refs'], enabled: !gated },
  );
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;
  // A MUNKATÁRS-szűrő opciói: a belső címtár (a kereső közös forrása, a teljes névsor egy lapon). A jog a
  // `/employees`-é (employees.view) — nélküle az opció-lista üres, a szűrő viszont az URL-ből továbbra is él.
  const employeesQ = useQuery({
    queryKey: ['employee-options', lang],
    queryFn: () => employeeSearch(lang)(''),
    enabled: !gated,
    staleTime: 5 * 60 * 1000,
  });

  // A `scope` a cache-kulcs része (a tab-váltás saját lapozott cache-t nyit — az optimista írások célja tiszta).
  const filters = { scope, q, status, category, type, usageLocation, team, holder, sort, order };
  const listKey = assetKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.assets.query({
        query: {
          scope,
          q: q || undefined,
          status: status === ALL ? undefined : status,
          category: category === ALL ? undefined : category,
          type: type === ALL ? undefined : type,
          usageLocation: usageLocation === ALL ? undefined : usageLocation,
          team: team === ALL ? undefined : team,
          holder: holder === ALL ? undefined : holder,
          sort,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.assets : [])),
    [pages],
  );

  // Deep-link: a `?open=<id>` eszközre nyitjuk a szerkesztő-sheetet, amint a sor az aktív fülön betöltött
  // (különben graceful no-op — a másik fül eszközére nincs by-id lekérés).
  useDeepLinkSheet<AssetRow>({
    id: scope === 'inventory' ? deepLinkOpenId : undefined,
    rows,
    getId: (a) => a.id,
    onOpen: (a) => setFormTarget({ asset: a }),
  });
  // Kérések fül: az igény-sáv a mélylink id-jára nyílik (nincs szükség a betöltött sorra — by-id lekérés).
  const requestLinkRows = useMemo(
    () => (deepLinkOpenId ? [{ id: deepLinkOpenId }] : []),
    [deepLinkOpenId],
  );
  useDeepLinkSheet<{ id: string }>({
    id: scope === 'request' ? deepLinkOpenId : undefined,
    rows: requestLinkRows,
    getId: (r) => r.id,
    onOpen: (r) => setRequestSheet({ id: r.id, seed: null }),
  });

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  // A BFF jelzi, ha a lista a védő-plafon miatt CSONKA (a darabszám és minden aggregátum is az) —
  // ezt kiírjuk a meta-sorba: egy csendben elvágott lista hibás döntést alapoz meg.
  const truncated = okPage?.status === 200 ? okPage.body.truncated : false;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters =
    q !== '' ||
    status !== ALL ||
    category !== ALL ||
    type !== ALL ||
    usageLocation !== ALL ||
    team !== ALL ||
    holder !== ALL;

  // Rendezés: azonos oszlopra kattintás → irányváltás; új oszlopra → az adott kulcs ASC.
  // Oszlop-fejléc kattintás: ugyanaz az oszlop → irány-váltás; MÁS oszlop → az új oszlop, `asc`-ből.
  const toggleSort = useCallback(
    (col: string) => {
      setFilters(
        col === sort ? { order: order === 'asc' ? 'desc' : 'asc' } : { sort: col, order: 'asc' },
      );
    },
    [sort, order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista lista-sor eltávolítás + rollback): a soft-delete a BFF `/assets/:id` (assets.delete).
  const removeMutation = useEntityMutation<{ id: string }, InfiniteData<ListPage>>({
    mutationFn: ({ id }) => api.assetDelete.mutation({ params: { id } }),
    invalidateKeys: [assetKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        if (!old) return old;
        const id = deleteTarget?.id;
        if (!id) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    assets: p.body.assets.filter((a) => a.id !== id),
                    total: Math.max(0, p.body.total - 1),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:assets.deleted'),
      conflict: t('toast:assets.conflict'),
      forbidden: t('toast:assets.forbidden'),
      validation: t('toast:assets.validation'),
      error: t('toast:assets.error'),
    },
  });

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
  }

  // Optimista státusz-átírás a lapozott lista-cache-ben (rollback a hookban). A scope-partíció szerver-oldali:
  // a nézetből kilépő státusz (pl. received a request-tabon) az onSettled-invalidáláskor kerül a másik tabra.
  const rewriteStatus = useCallback(
    (
      old: InfiniteData<ListPage> | undefined,
      id: string | undefined,
      nextStatus: string,
    ): InfiniteData<ListPage> | undefined => {
      if (!old || !id) return old;
      return {
        ...old,
        pages: old.pages.map((p) =>
          p.status === 200
            ? {
                ...p,
                body: {
                  ...p.body,
                  assets: p.body.assets.map((a) =>
                    a.id === id ? { ...a, status: nextStatus } : a,
                  ),
                },
              }
            : p,
        ),
      };
    },
    [],
  );

  // Jóváhagyás (decide approve) — CSAK requested státuszból; If-Match/OCC; optimista: requested → approved.
  const approveMutation = useEntityMutation<
    { id: string; version: number },
    InfiniteData<ListPage>
  >({
    mutationFn: ({ id, version }) =>
      api.assetDecide.mutation({
        params: { id },
        // A rejectReason explicit null — a BFF a NEM-üres indokot tiltja approve mellett (422).
        body: { decision: 'approve', rejectReason: null },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [assetKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => rewriteStatus(old, workflowTarget?.asset.id, 'approved'),
    },
    messages: {
      success: t('toast:assets.requestApproved'),
      conflict: t('toast:assets.conflict'),
      forbidden: t('toast:assets.forbidden'),
      validation: t('toast:assets.validation'),
      error: t('toast:assets.error'),
    },
  });

  // Elutasítás (decide reject) — opcionális indokkal; optimista: requested → rejected.
  const rejectMutation = useEntityMutation<
    { id: string; version: number; reason: string },
    InfiniteData<ListPage>
  >({
    mutationFn: ({ id, version, reason }) =>
      api.assetDecide.mutation({
        params: { id },
        body: { decision: 'reject', rejectReason: reason === '' ? null : reason },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [assetKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => rewriteStatus(old, rejectTarget?.id, 'rejected'),
    },
    messages: {
      success: t('toast:assets.requestRejected'),
      conflict: t('toast:assets.conflict'),
      forbidden: t('toast:assets.forbidden'),
      validation: t('toast:assets.validation'),
      error: t('toast:assets.error'),
    },
  });

  // Beszerzés-előrehaladás (progress) — szigorú sorrend a BFF-en (sértés → 409); optimista: státusz → lépés.
  const progressMutation = useEntityMutation<
    { id: string; version: number; step: Exclude<WorkflowAction, 'approve'> },
    InfiniteData<ListPage>
  >({
    mutationFn: ({ id, version, step }) =>
      api.assetProgress.mutation({
        params: { id },
        body: { step },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [assetKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) =>
        rewriteStatus(old, workflowTarget?.asset.id, workflowTarget?.action ?? 'being_procured'),
    },
    messages: {
      success: t('toast:assets.progressSaved'),
      conflict: t('toast:assets.conflict'),
      forbidden: t('toast:assets.forbidden'),
      validation: t('toast:assets.validation'),
      error: t('toast:assets.error'),
    },
  });

  // A megerősítő dialógus (approve / következő lépés) jóváhagyása → a megfelelő mutáció.
  function confirmWorkflow(): void {
    if (!workflowTarget) return;
    const { asset, action } = workflowTarget;
    const vars = { id: asset.id, version: asset.version };
    if (action === 'approve') {
      approveMutation.mutate(vars, { onSettled: () => setWorkflowTarget(null) });
    } else {
      progressMutation.mutate(
        { ...vars, step: action },
        { onSettled: () => setWorkflowTarget(null) },
      );
    }
  }

  function confirmReject(reason: string): void {
    if (!rejectTarget) return;
    rejectMutation.mutate(
      { id: rejectTarget.id, version: rejectTarget.version, reason },
      { onSettled: () => setRejectTarget(null) },
    );
  }

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;

  // Egy személy (birtokló/igénylő) színes avatar + név (formatName, locale-sorrend) — a Munkatársak/Kapcsolat-
  // tartók/Support-csapatok mintája. Üres név → a hívó dönti el a fallbacket.
  const renderPerson = (
    name: string | null,
    givenName: string | null,
    familyName: string | null,
    avatarUrl: string | null,
    color: string | null,
  ): JSX.Element | null => {
    if (!name) return null;
    // A KÖZÖS `PersonChip` (avatar + locale-tudatos név, örökölt cella-tipográfia). A `gap-1.5` felülírás
    // szándékos: a szervezeti felépítés „Munkatársak" oszlopával azonos sűrűség (a `twMerge` a chip
    // `gap-2`-jét váltja le).
    return (
      <PersonChip
        givenName={givenName}
        familyName={familyName}
        name={name}
        avatarUrl={avatarUrl}
        accentColor={color}
        size="sm"
        className="gap-1.5"
      />
    );
  };

  // EXPORT: a sorok a lista-végpontról, az AKTÍV FÜLLEL (`scope`) és minden szűrővel — a leltár- és az
  // igénylés-fül tehát KÜLÖN fájlt ad, a saját oszlopaival (a `columns` is fül-függő).
  const fetchExportPage = useCallback(
    async (offset: number | undefined) => {
      const r = await api.assets.query({
        query: {
          scope,
          q: q || undefined,
          status: status === ALL ? undefined : status,
          category: category === ALL ? undefined : category,
          type: type === ALL ? undefined : type,
          usageLocation: usageLocation === ALL ? undefined : usageLocation,
          team: team === ALL ? undefined : team,
          holder: holder === ALL ? undefined : holder,
          sort,
          order,
          limit: 200,
          offset: offset ?? 0,
        },
      });
      if (r.status !== 200) return null;
      return { rows: r.body.assets, next: r.body.nextOffset };
    },
    [scope, q, status, category, type, usageLocation, team, holder, sort, order],
  );

  // Személy EXPORT-alakja: locale-tudatos név (a `renderPerson` szöveges párja, avatar nélkül).
  const personExport = (
    name: string | null,
    givenName: string | null,
    familyName: string | null,
  ): string =>
    name ? formatName({ givenName, familyName, displayName: name }, i18n.language) : '';

  // A „Kinek" szöveges alakja (export): iroda / a kedvezményezett / önmagának igénylésnél az igénylő.
  const kinekText = (a: AssetRow): string => {
    if (a.officeUse) return t('assets.request.officeRecipient');
    return a.requestedForName
      ? personExport(a.requestedForName, a.requestedForGivenName, a.requestedForFamilyName)
      : personExport(a.requesterName, a.requesterGivenName, a.requesterFamilyName);
  };

  // Közös oszlopok (mindkét tabon): Eszköz (név + „> Részletek") · Típus · Státusz.
  const colAsset: DataTableColumn<AssetRow> = {
    // Eszköz — név (itemTitle) + „> Részletek" toggle (kinyíló azonosító-mezők; lásd AssetNameCell).
    id: 'name',
    header: t('assets.name'),
    sortable: true,
    cardLabel: t('assets.name'),
    width: 'minmax(0,2.4fr)',
    multiline: true,
    // A név MELLETT a „Részletek" alatti azonosító-/vásárlás-mezők is (leltár-ív, sorozatszám, számla) —
    // ezek nélkül az export nem használható eszköz-nyilvántartásra. `label: érték` párokként, sortöréssel.
    exportValue: (a) =>
      [
        a.name,
        ...assetDetailRows(a, t, lookup.isSoftwareType(a.type)).map(
          (r) => `${r.label}: ${r.value}`,
        ),
      ].join('\n'),
    // Az igénylés-fülön a részleg-badge itt jelenik meg (a „Részletek" gomb mellett) — külön oszlop helyett.
    cell: (a) => <AssetNameCell asset={a} showTeam />,
    // MOBIL: a fejlécbe csak a név (+ részleg-badge), az azonosító-mezők a kártya „Részletek" blokkjába —
    // így a kártyán PONTOSAN EGY kibontó van (a `DataTable` `cardCell`/`cardExtra` propjai).
    cardCell: (a) => <AssetNameCardCell asset={a} showTeam />,
    // MOBIL: a részletek label:érték PÁRKÉNT mennek át (nem kész JSX-ként), hogy a `DataTable` UGYANABBA a
    // rácsba fűzze őket a többi oszlop soraival — így egyetlen címke-kolumna van, és minden érték a
    // leghosszabb címkéhez igazodva, egy vonalban kezdődik. (A DESKTOP-változat marad a cella saját,
    // kompakt, vonallal jelölt kibontója — ott a blokk egy cellán belül van.)
    // A MOBIL-kártya ugyanazt a link-felismerést kapja, mint a desktop kibontó (egy szabály, két kontextus).
    cardExtraItems: (a) =>
      assetDetailRows(a, t, lookup.isSoftwareType(a.type)).map((r) => ({
        label: r.label,
        value: <DetailValue value={r.value} />,
      })),
  };
  const colType: DataTableColumn<AssetRow> = {
    // Típus — a konkrét típus SZÖVEGGEL (a Munkatársak-lista beosztás-oszlopának stílusa: örökölt cella-szín/-méret),
    // alatta a fő-kategória (hardware/software) színes, ikonos badge-e (a Munkatársak eszközök-oszlopának ikonjaival).
    id: 'type',
    header: t('assets.type'),
    sortable: true,
    cardLabel: t('assets.type'),
    width: 'minmax(0,1.3fr)',
    multiline: true,
    exportValue: (a) =>
      [a.type ? lookup.typeName(a.type, t) : '', lookup.categoryName(a.category, t)]
        .filter(Boolean)
        .join(' · '),
    // Mobil-kártya: a 2. sor (subtitle) — a típus + a birtokló/igénylő (holderName/requesterName) `·`-tal fűzve.
    card: 'subtitle',
    cell: (a) =>
      a.type ? (
        <AssetTypeWithCategory type={a.type} category={a.category} lookup={lookup} />
      ) : (
        emDash
      ),
  };
  const colStatus: DataTableColumn<AssetRow> = {
    // Státusz — státusz-badge (9-állapotú életút), UTOLSÓ oszlop. A badge szövege KISBETŰS (csak vizuális).
    id: 'status',
    header: t('assets.status'),
    sortable: true,
    cardLabel: t('assets.status'),
    width: 'minmax(0,1.5fr)',
    multiline: true,
    exportValue: (a) =>
      a.status ? t(`assets.statusLabel.${a.status}`, { defaultValue: a.status }) : '',
    cell: (a) =>
      a.status ? (
        <AssetStatusBadge
          status={a.status}
          label={t(`assets.statusLabel.${a.status}`, { defaultValue: a.status })}
        />
      ) : (
        emDash
      ),
  };

  // ── 1. TAB (inventory) oszlopai: Eszköz · Típus · Felhasználás helye · Munkatárs · Státusz (a vásárlás-adatok az Eszköz „Részletek" alatt) ──
  const inventoryColumns: DataTableColumn<AssetRow>[] = [
    colAsset,
    colType,
    {
      // Munkatárs — színes avatar + név (formatName); ha nincs holder, az igénylő „(igénylő)". A felhasználás
      // helye NEM itt van (kikerült a név alatti chipbe az Eszköz oszlopban).
      id: 'holderName',
      header: t('assets.holder'),
      sortable: true,
      cardLabel: t('assets.holder'),
      width: 'minmax(0,1.7fr)',
      multiline: true,
      // A cella logikája: birtokló, ha van; különben az igénylő „(igénylő)" jelöléssel — az export ugyanezt adja.
      exportValue: (a) =>
        a.holderName
          ? personExport(a.holderName, a.holderGivenName, a.holderFamilyName)
          : a.requesterName
            ? `${personExport(a.requesterName, a.requesterGivenName, a.requesterFamilyName)} (${t('assets.requester')})`
            : a.officeUse
              ? t('assets.request.officeRecipient')
              : '',
      // Mobil-kártya (leltár fül): a 2. sor (subtitle) — a birtokló/igénylő a típus mellé fűzve.
      card: 'subtitle',
      cell: (a) => {
        const holder = renderPerson(
          a.holderName,
          a.holderGivenName,
          a.holderFamilyName,
          a.holderAvatarUrl,
          a.holderTeamColor,
        );
        const requester = renderPerson(
          a.requesterName,
          a.requesterGivenName,
          a.requesterFamilyName,
          a.requesterAvatarUrl,
          a.requesterTeamColor,
        );
        return (
          <div className="min-w-0 space-y-0.5">
            {holder ??
              requester ??
              (a.officeUse ? (
                <span>{t('assets.request.officeRecipient')}</span>
              ) : (
                <span className="text-muted-foreground">{t('assets.noHolder')}</span>
              ))}
            {!holder && requester && (
              <Text variant="secondary" as="div" className="truncate">
                {t('assets.requester')}
              </Text>
            )}
          </div>
        );
      },
    },
    colStatus,
  ];

  // ── 2. TAB (request) oszlopai: Eszköz (+ részleg-badge a „Részletek" mellett) · Típus · Ár · Igénylő · Státusz ──
  const requestColumns: DataTableColumn<AssetRow>[] = [
    colAsset,
    colType,
    {
      // Ár — a legacy igény-tab ár-cellája: nettó / áfa / (elválasztó) / bruttó — a címke BALRA, az érték JOBBRA.
      // A bruttó = nettó × (1 + áfa/100), 2 tizedesre kerekítve. Rendezés: netPrice (numerikus a BFF-ben).
      id: 'netPrice',
      header: t('assets.price'),
      sortable: true,
      cardLabel: t('assets.price'),
      width: 'minmax(0,1.7fr)',
      multiline: true,
      // A három érték (nettó/áfa/bruttó) egy cellában, `label: érték` párokként — a bruttó SZÁMÍTOTT
      // (nettó × (1+áfa/100)), ugyanazzal a kerekítéssel, mint a cella.
      exportValue: (a) => {
        const net = a.netPrice ? Number(a.netPrice) : NaN;
        if (Number.isNaN(net)) return '';
        const vatPct = a.vatRate && !Number.isNaN(Number(a.vatRate)) ? Number(a.vatRate) : 0;
        const gross = Math.round(net * (1 + vatPct / 100) * 100) / 100;
        return [
          `${t('assets.priceNet')}: ${fmtHuf(net)}`,
          `${t('assets.priceVat')}: ${fmtPercent(vatPct)}`,
          `${t('assets.priceGross')}: ${fmtHuf(gross)}`,
        ].join('\n');
      },
      // Mobil-kártya: REJTVE — az ár nettó/áfa/bruttó bontás-blokk, nem „tiszta" egy-összeg → nem trailing-anyag.
      card: 'hidden',
      cell: (a) =>
        priceParts(a.netPrice, a.vatRate) ? (
          <AssetPriceBreakdown netPrice={a.netPrice} vatRate={a.vatRate} />
        ) : (
          emDash
        ),
    },
    {
      // Kinek (#115) — kinek igényelték: iroda → „Iroda (általános használat)"; munkatárs → ő; önmagának
      // igénylésnél az igénylő. Az igénylő színes avatarja a saját oszlopában van, itt a név elég.
      id: 'kinek',
      header: t('assets.request.kinekCol'),
      cardLabel: t('assets.request.kinekCol'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      exportValue: (a) => kinekText(a),
      card: 'hidden',
      cell: (a) => {
        if (a.officeUse) return <span>{t('assets.request.officeRecipient')}</span>;
        const name = a.requestedForName ?? a.requesterName;
        return name ? (
          <PersonChip
            givenName={a.requestedForName ? a.requestedForGivenName : a.requesterGivenName}
            familyName={a.requestedForName ? a.requestedForFamilyName : a.requesterFamilyName}
            name={name}
            avatarUrl={a.requestedForName ? null : a.requesterAvatarUrl}
            accentColor={a.requestedForName ? null : a.requesterTeamColor}
            size="sm"
            className="gap-1.5"
          />
        ) : (
          emDash
        );
      },
    },
    {
      // Igénylő — a kérelmező színes avatar + név (formatName), a Státusz ELŐTT (user-döntés). Rendezés: requesterName.
      id: 'requesterName',
      header: t('assets.requesterCol'),
      sortable: true,
      cardLabel: t('assets.requesterCol'),
      width: 'minmax(0,1.7fr)',
      multiline: true,
      exportValue: (a) =>
        personExport(a.requesterName, a.requesterGivenName, a.requesterFamilyName),
      // Mobil-kártya (igénylés fül): a 2. sor (subtitle) — az igénylő a típus mellé fűzve.
      card: 'subtitle',
      cell: (a) =>
        renderPerson(
          a.requesterName,
          a.requesterGivenName,
          a.requesterFamilyName,
          a.requesterAvatarUrl,
          a.requesterTeamColor,
        ) ?? emDash,
    },
    colStatus,
  ];

  const columns = scope === 'request' ? requestColumns : inventoryColumns;

  const exportCtl = useListExport<AssetRow, number>({
    columns,
    // A fájlnév a FÜLET is jelzi: az igénylés-lista más oszlop-készlet, mint a leltár.
    slug: scope === 'request' ? 'asset-requests' : 'assets',
    fetchPage: fetchExportPage,
    filters: {
      q,
      scope,
      status: status === ALL ? undefined : status,
      category: category === ALL ? undefined : category,
      type: type === ALL ? undefined : type,
    },
  });

  // Törlés-gomb (mindkét tabon — a legacy szerint a workflow-sor is törölhető admin joggal) — KÖZÖS DeleteRowAction.
  const deleteButton = (a: AssetRow): JSX.Element => (
    <DeleteRowAction label={t('assets.delete')} onClick={() => setDeleteTarget(a)} />
  );

  // LELTÁR (inventory) sor-akciói: ceruza + kuka (KÖZÖS komponensek). Szerkeszthető minden leltári sor — a `received`
  // is (legacy-parity: a beérkezett eszköz státuszát kézzel állítják tovább, a beszerzési adatok utólag itt kerülnek
  // fel); a (ritkán) folyamatban lévő workflow-sor ceruzája TILTOTT (a meglévő tooltippel; a szerver is 409-cel véd).
  const inventoryRowActions = (a: AssetRow): JSX.Element => {
    const editable = !isWorkflowRow(a);
    return (
      <RowActions>
        {canManage &&
          (editable ? (
            <EditRowAction label={t('assets.edit')} onClick={() => setFormTarget({ asset: a })} />
          ) : (
            <RowActionButton
              label={t('assets.edit')}
              icon={PencilSquareIcon}
              onClick={() => setFormTarget({ asset: a })}
              disabled
              disabledTooltip={t('assets.editWorkflowDisabled')}
            />
          ))}
        {canDelete && deleteButton(a)}
      </RowActions>
    );
  };

  // IGÉNYLÉSEK (request) sor-akciói a státusz szerint (KÖZÖS RowActionButton): requested → jóváhagyás + elutasítás
  // (asset_requests.decide; az elutasítás destruktív tónus); approved/being_procured/purchased → a következő
  // beszerzési lépés (asset_requests.manage); + kuka.
  const requestRowActions = (a: AssetRow): JSX.Element => {
    const nextStep = NEXT_STEP[a.status];
    return (
      <RowActions>
        <ViewRowAction
          label={t('assets.request.view')}
          onClick={() => setRequestSheet({ id: a.id, seed: a })}
        />
        {canDecide && a.status === 'requested' && (
          <>
            <RowActionButton
              label={t('assets.workflow.approve.action')}
              icon={CheckCircleIcon}
              onClick={() => setWorkflowTarget({ asset: a, action: 'approve' })}
            />
            <RowActionButton
              label={t('assets.workflow.reject.action')}
              icon={XCircleIcon}
              onClick={() => setRejectTarget(a)}
              destructive
            />
          </>
        )}
        {canRequest && nextStep && (
          <RowActionButton
            label={t(`assets.workflow.${nextStep}.action`)}
            icon={ArrowRightCircleIcon}
            onClick={() => setWorkflowTarget({ asset: a, action: nextStep })}
          />
        )}
        {canDelete && deleteButton(a)}
      </RowActions>
    );
  };

  const hasRowActions = scope === 'inventory' ? canManage || canDelete : true;
  const renderRowActions = scope === 'inventory' ? inventoryRowActions : requestRowActions;
  // Az akció-oszlop szélessége az egy sorban EGYSZERRE lehetséges gombok maximumához igazodik
  // (a decide-páros és a lépés-gomb sosem jelenik meg ugyanazon a soron).
  const maxButtons =
    scope === 'inventory'
      ? (canManage ? 1 : 0) + (canDelete ? 1 : 0)
      : 1 + Math.max(canDecide ? 2 : 0, canRequest ? 1 : 0) + (canDelete ? 1 : 0);
  const actionsColWidth =
    maxButtons >= 3 ? '9rem' : maxButtons === 2 ? '7rem' : maxButtons === 1 ? '4rem' : '0';

  return (
    <AppShell
      // A mobil FAB az AKTÍV tab create-jét nyitja: Meglévő → „Új eszköz" (canManage), Igényelt → „Igénylés beadása"
      // (canRequest). Desktopon ugyanezt a toolbar tab-érzékeny ListNewButton-ja adja (`hidden md:block`).
      secondaryActions={[
        // MOBIL: a sáv export-gombja rejtve van — a FAB-ban elérhető marad (`xlsx`, formátum-választás
        // nélkül: a FAB egy koppintásos felület).
        {
          key: 'export',
          icon: <ArrowDownTrayIcon />,
          label: t('common.export.label'),
          onClick: () => exportCtl.run('xlsx'),
        },
        // a katalógus-kezelő mobilon is elérhető (a sáv gombja ≥md)
        ...(canManage
          ? [
              {
                key: 'catalog',
                icon: <Squares2X2Icon />,
                label: t('assets.catalog.button'),
                onClick: () => setCatalogOpen(true),
              },
            ]
          : []),
      ]}
      primaryAction={
        scope === 'inventory'
          ? canManage
            ? {
                icon: <PlusIcon className="size-5 stroke-2" />,
                label: t('assets.new'),
                onClick: () => setFormTarget({ asset: null }),
              }
            : undefined
          : canRequest
            ? {
                icon: <PlusIcon className="size-5 stroke-2" />,
                label: t('assets.request.new'),
                onClick: () => setRequestSheet({ id: null, seed: null }),
              }
            : undefined
      }
      toolbar={
        // A tab-váltó (underline-tabs + ikon) a KERESŐ FÖLÖTT — a jogosultságok-oldal mintája (egységes minta).
        // Alatta a KÖZÖS ListToolbar: kereső + „Szűrők (N)" popover (státusz → típus) + darabszám-meta + New gomb.
        <div className="flex flex-col">
          <div className="px-4 pt-2">
            <Tabs value={scope} onValueChange={onScopeChange}>
              <TabsList>
                <TabsTrigger value="inventory">
                  <CubeIcon />
                  {t('assets.tabInventory')}
                </TabsTrigger>
                <TabsTrigger value="request">
                  <ClipboardDocumentListIcon />
                  {t('assets.tabRequests')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <ListToolbar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: t('common.searchPlaceholder'),
            }}
            filters={[
              // 1. Felhasználás helye — ELSŐ szűrő (user-döntés: ez a leggyakrabban használt
              // szűkítés, és az oszlopa kikerült a táblából → a szűrő az elsődleges belépési pontja).
              // CSAK a leltár-fülön (az igénylés-tabon nincs értelme).
              ...(scope === 'inventory'
                ? [
                    {
                      key: 'usageLocation',
                      label: t('assets.usageLocationCol'),
                      value: usageLocation,
                      onChange: setUsageLocation,
                      options: USAGE_OPTIONS.map((u) => ({
                        value: u,
                        label: t(`assets.usageLabel.${u}`, { defaultValue: u }),
                      })),
                      allValue: ALL,
                    },
                  ]
                : []),
              {
                // 2. Kategória (hardver/szoftver) — ez szűkíti a típus-opciókat is.
                key: 'category',
                label: t('assets.filterCategory'),
                value: category,
                onChange: onCategoryChange,
                options: lookup.categories.map((c) => ({
                  value: c.key,
                  label: catalogText(c.name, lang),
                })),
                preserveCase: true,
                allValue: ALL,
              },
              {
                // 3. Típus — a kiválasztott kategória típusaira szűkítve (kategória=mind → az összes).
                key: 'type',
                label: t('assets.filterType'),
                value: type,
                onChange: (v: string) => setFilters({ type: v }),
                options: typeSource.map((ty) => ({
                  value: ty.key,
                  label: catalogText(ty.name, lang),
                })),
                preserveCase: true,
                allValue: ALL,
              },
              {
                // 4. CSAPAT — MINDKÉT fülön. Ez nem kényelmi szűrő: a csapat-törlés guardja (409, „eszköz
                // tartozik hozzá") enélkül megválaszolhatatlan kérdést tett fel — a leltár-fül a csapatot
                // meg sem mutatta, tehát nem lehetett kideríteni, MELYIK eszközt kell átrakni. Az érték a
                // csapat UUID-ja (a név átnevezésre elcsúszna); a csapat-lista a szerkesztő refjéből jön,
                // tehát nincs új végpont.
                key: 'team',
                label: t('assets.filterTeam'),
                value: team,
                onChange: (v: string) => setFilters({ team: v }),
                options: (refs?.teams ?? []).map((tm) => ({ value: tm.id, label: tm.name })),
                allValue: ALL,
              },
              {
                // 5. MUNKATÁRS (#112) — a Csapat és a Státusz közt, MINDKÉT fülön: a birtokos, igénynél a „kinek"
                // (önmagának igénylésnél az igénylő). A szerver illeszt; az érték az identity UUID-ja. Tulajdonnév
                // → eredeti kis-/nagybetű, keresős legördülő (57 fős névsor).
                key: 'holder',
                label: t('assets.filterHolder'),
                value: holder,
                onChange: (v: string) => setFilters({ holder: v }),
                options: (employeesQ.data ?? []).map((e) => ({ value: e.id, label: e.label })),
                allValue: ALL,
                preserveCase: true,
                searchable: true,
              },
              {
                // 6. Státusz — az opciók a tab-scope szerint (a BFF is ezen a partíción szűr).
                key: 'status',
                label: t('assets.filterStatus'),
                value: status,
                onChange: (v: string) => setFilters({ status: v }),
                options: (scope === 'request'
                  ? REQUEST_STATUS_OPTIONS
                  : INVENTORY_STATUS_OPTIONS
                ).map((s) => ({
                  value: s,
                  label: t(`assets.statusLabel.${s}`, { defaultValue: s }),
                })),
                allValue: ALL,
              },
            ]}
            meta={
              infinite.isFetching && !infinite.isFetchingNextPage
                ? t('assets.loading')
                : !isError && !infinite.isLoading
                  ? t('assets.countOf', { shown: rows.length, total }) +
                    (truncated ? ` · ${t('common.listTruncated')}` : '')
                  : ''
            }
            actions={
              // A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a tab-érzékeny létrehozást.
              <>
                <div className="hidden md:block">
                  <ListExportButton
                    onExport={exportCtl.run}
                    pending={exportCtl.pending}
                    hasRows={!isError && rows.length > 0}
                  />
                </div>
                {canManage && (
                  <div className="hidden md:block">
                    <Button variant="outline" type="button" onClick={() => setCatalogOpen(true)}>
                      <Squares2X2Icon className="mr-2 size-4" />
                      {t('assets.catalog.button')}
                    </Button>
                  </div>
                )}
                <div className="hidden md:block">
                  {scope === 'inventory' ? (
                    <ListNewButton
                      label={t('assets.new')}
                      onClick={() => setFormTarget({ asset: null })}
                      enabled={canManage}
                      disabledTooltip={t('common.noCreatePermission')}
                    />
                  ) : (
                    <ListNewButton
                      label={t('assets.request.new')}
                      onClick={() => setRequestSheet({ id: null, seed: null })}
                      enabled={canRequest}
                      disabledTooltip={t('common.noCreatePermission')}
                    />
                  )}
                </div>
              </>
            }
          />
        </div>
      }
    >
      <div className="flex h-full min-h-0 flex-col px-4 pb-2">
        <DataTable<AssetRow>
          columns={columns}
          rows={rows}
          getRowId={(a) => a.id}
          sort={sort}
          order={order}
          onSort={toggleSort}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState title={t('assets.noMatches')} description={t('assets.noMatchesDesc')} />
            ) : (
              <EmptyState title={t('assets.empty')} description={t('assets.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('assets.actions')}
          actionsColWidth={actionsColWidth}
          rowActions={hasRowActions ? renderRowActions : undefined}
          {...(scope === 'request'
            ? { onRowClick: (a: AssetRow) => setRequestSheet({ id: a.id, seed: a }) }
            : {})}
        />
      </div>

      <AssetFormSheet
        open={formTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFormTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?open` param törlése (back-barát + újra-nyitható)
          }
        }}
        asset={formTarget?.asset ?? null}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('assets.deleteTitle')}
        description={t('assets.deleteDesc', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('assets.delete')}
        cancelLabel={t('assets.cancel')}
        pendingLabel={t('assets.deleting')}
        tone="danger"
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />

      {/* Igénylés beadása (workflow B) — jobb oldali FormSheet. */}
      <AssetRequestSheet
        open={requestSheet !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRequestSheet(null);
            onClearDeepLink?.(); // mélylink: záráskor a `open` param törlése (újra-nyitható)
          }
        }}
        requestId={requestSheet?.id ?? null}
        seed={requestSheet?.seed ?? null}
      />

      <AssetCatalogSheet
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        categories={lookup.categories}
        canManage={canManage}
      />

      {/* Jóváhagyás / következő beszerzési lépés — semleges megerősítő (a cím/leírás az akció szerint). */}
      <ConfirmDialog
        open={workflowTarget !== null}
        onOpenChange={(open) => !open && setWorkflowTarget(null)}
        title={t(`assets.workflow.${workflowTarget?.action ?? 'approve'}.title`)}
        description={t(`assets.workflow.${workflowTarget?.action ?? 'approve'}.desc`, {
          name: workflowTarget?.asset.name ?? '',
        })}
        confirmLabel={t(`assets.workflow.${workflowTarget?.action ?? 'approve'}.confirm`)}
        cancelLabel={t('assets.cancel')}
        pendingLabel={t('assets.workflow.working')}
        tone="default"
        pending={approveMutation.isPending || progressMutation.isPending}
        onConfirm={confirmWorkflow}
      />

      {/* Elutasítás — indok-mezős dedikált dialógus (a ConfirmDialog nem fogad extra tartalmat). */}
      <AssetRejectDialog
        open={rejectTarget !== null}
        onOpenChange={(open) => !open && setRejectTarget(null)}
        assetName={rejectTarget?.name ?? ''}
        pending={rejectMutation.isPending}
        onConfirm={confirmReject}
      />
    </AppShell>
  );
}
